import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReportAssistenciaTecnicaForm, ReportAssistenciaTecnicaSchema } from "../../../schemas/AssistenciaTecnica_lista/report_assistencia_tecnica";
import { useEffect, useMemo } from "react";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { ClienteDropDown } from "../../../../../../common/domain/models/cliente_dropdown";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FormRangeDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_range_picker_input";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { CategoriaAssistenciaDropDown } from "../../../../domain/models/categoria_assistencia_dropdown";

/**
 * ReportAssistenciaTecnicaModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  clientesDropDown: ClienteDropDown[];
  centroCustosDropDown: CentroCustoDropDown[];
  categoriaDropDown: CategoriaAssistenciaDropDown[];
  onClose: () => void;
  onSubmit: (form: ReportAssistenciaTecnicaForm) => void;
};

export const ReportAssistenciaTecnicaModal = ({
  open,
  saving,
  loading,
  clientesDropDown,
  centroCustosDropDown,
  categoriaDropDown,
  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<ReportAssistenciaTecnicaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ReportAssistenciaTecnicaSchema),
  });

  const statusOptions = useMemo(() => {
    return [
      { id: 0, label: 'Todas' },
      { id: 1, label: 'Aberta' },
      { id: 2, label: 'Encerrada' },
    ];
  }, [
  ]);

  const prioridadeOptions = useMemo(() => {
    return [
      { id: 3, label: 'Todas' },
      { id: 0, label: 'Baixa' },
      { id: 1, label: 'Média' },
      { id: 2, label: 'Alta' },
    ];
  }, [
  ]);

  const procedenteOptions = useMemo(() => {
    return [
      { id: 0, label: 'Aguardando Vistoria' },
      { id: 1, label: 'Sim' },
      { id: 2, label: 'Não' },
    ];
  }, [
  ]);

  const resolvidoOptions = useMemo(() => {
    return [
      { id: 5, label: 'Todas Em Aberto' },
      { id: 0, label: 'Aguardando Reparo' },
      { id: 3, label: 'Reparo Iniciado' },
      { id: 1, label: 'Sim' },
      { id: 2, label: 'Não' },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      hookForm.reset({ status: 0, });
    }
  }, [
    open,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title='Relatório de Assistência Técnica'
      confirmButtonLabel="Gerar"
      confirmSavingButtonLabel="Gerando..."
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
        <DefaultFormInput
          name='codigo'
          label='Código Sistema'
          reactHookForm={hookForm}
        />

        <DefaultFormInput
          name='nroChamado'
          label='Número do Chamado'
          reactHookForm={hookForm}
        />

        <DefaultFormSelectInput
          name='status'
          label='Status'
          showSelectionLabel={false}
          options={statusOptions}
          reactHookForm={hookForm}
        />

        <FormRangeDatePickerInput
          name='dataAbertura'
          label='Data'
          reactHookForm={hookForm}
        />

        <DefaultFormInput
          name='unidade'
          label='Unidade'
          reactHookForm={hookForm}
        />

        <DefaultFormMultiAutocomplete
          name='procedente'
          label='Procedente'
          options={procedenteOptions}
          getOptionKey={x => x.id}
          getOptionLabel={x => x.label}
          reactHookForm={hookForm}
        />

        <DefaultFormMultiAutocomplete
          name='categoria'
          label='Categoria'
          options={categoriaDropDown}
          getOptionKey={x => x.descricao}
          getOptionLabel={x => x.descricao}
          reactHookForm={hookForm}
        />

        <DefaultFormMultiAutocomplete
          name='resolvido'
          label='Resolvido'
          options={resolvidoOptions}
          getOptionKey={x => x.id}
          getOptionLabel={x => x.label}
          reactHookForm={hookForm}
        />

        <FormRangeDatePickerInput
          name='dataEncerramento'
          label='Data Encerramento'
          reactHookForm={hookForm}
        />

        <DefaultFormMultiAutocomplete
          name='clientes'
          label='Cliente'
          options={clientesDropDown}
          getOptionKey={x => x.id}
          getOptionLabel={x => x.title}
          reactHookForm={hookForm}
        />

        <DefaultFormMultiAutocomplete
          name='centroCustos'
          label='Centro de Custo'
          options={centroCustosDropDown}
          getOptionKey={x => x.id}
          getOptionLabel={x => x.title}
          reactHookForm={hookForm}
        />

        <DefaultFormMultiAutocomplete
          name='prioridade'
          label='Prioridade'
          options={prioridadeOptions}
          getOptionKey={x => x.id}
          getOptionLabel={x => x.label}
          reactHookForm={hookForm}
        />
      </div>
    </BaseFormControlledModal>
  );
}